import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  signal,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { StorageService } from '@core/services/storage.service';
import { ButtonSize, ButtonTypes } from '@shared/modules/ui/components/button/button.component';
import { ConfiguratorStoreService } from './services/configurator-store.service';
import { buildReview, buildSummary, countSummaryItems } from './helpers/configurator-summary.helper';
import { SnackBarService } from '@core/services/snackbar.service';
import { NavigateService } from '@shared/services/navigate.service';
import { ConfiguratorPersistenceService } from './services/configurator-persistence.service';
import { ConfiguratorFilesService } from './services/configurator-files.service';
import { AttachmentsManageService } from '@shared/modules/attachment-manage/services/attachments-manage.service';
import {
  CONFIGURATOR_PERSISTENCE_ADAPTER,
  ConfiguratorLocalDraftAdapter
} from './services/configurator-persistence.adapter';

@Component({
  selector: 'app-projects-configurator-v2',
  templateUrl: './projects-configurator-v2.component.html',
  styleUrls: ['./projects-configurator-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    ConfiguratorStoreService,
    ConfiguratorPersistenceService,
    ConfiguratorFilesService,
    AttachmentsManageService,
    // No configurator API yet (docs/project-configurator-v2/PLAN.md, D2): drafts are kept in the browser.
    { provide: CONFIGURATOR_PERSISTENCE_ADAPTER, useClass: ConfiguratorLocalDraftAdapter }
  ]
})
export class ProjectsConfiguratorV2Component implements OnInit {
  protected readonly store = inject(ConfiguratorStoreService);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly translate = inject(TranslateService);
  private readonly storage = inject(StorageService);
  private readonly snackBar = inject(SnackBarService);
  private readonly navigate = inject(NavigateService);
  private readonly files = inject(ConfiguratorFilesService);
  private projectId: string | null = null;

  // Emits once the translations are loaded and on language changes, so the summary is built again.
  private readonly translations = toSignal(this.translate.stream('ProjectConfiguratorV2.Summary.title'));

  @ViewChild('top') top: ElementRef<HTMLElement>;

  protected readonly ButtonSize = ButtonSize;
  protected readonly ButtonTypes = ButtonTypes;
  protected readonly stepLabels = this.store.steps.map(
    (step) => `ProjectConfiguratorV2.Steps.${step.id}.name`
  );

  private readonly formatContext = {
    translate: (key: string, params?: Record<string, unknown>) => this.translate.instant(key, params),
    decimalSymbol: this.storage.decimalSymbol || ','
  };

  protected readonly summary = computed(() => {
    this.translations();
    return buildSummary(
      this.store.steps,
      this.store.answers(),
      this.store.currentStep(),
      this.store.maxStep(),
      this.formatContext
    );
  });
  protected readonly review = computed(() => {
    this.translations();
    return this.store.isReview()
      ? buildReview(this.store.steps, this.store.answers(), this.formatContext)
      : [];
  });
  protected readonly summaryCount = computed(() => countSummaryItems(this.summary()));
  /** Bottom sheet of the summary below the single-column width. */
  protected readonly summaryOpen = signal(false);

  ngOnInit(): void {
    const projectId = this.route.snapshot.pathFromRoot
      .map((route) => route.paramMap.get('projectId'))
      .find(Boolean);
    if (!projectId) return;

    this.projectId = projectId;
    this.store
      .init(`project-${projectId}`)
      .pipe(takeUntilDestroyed(this.destroyRef))
      // The files are found through the entry ids stored in the answers.
      .subscribe(() => this.files.init(Number(projectId)));
  }

  next(): void {
    if (this.store.next()) {
      this.scrollToTop();
      return;
    }
    this.scrollToFirstError();
  }

  /** "Anfrage absenden" on the review. Sending to the API follows once it exists (PLAN, stage 8b). */
  submit(): void {
    if (this.store.submit()) {
      this.scrollToTop();
      return;
    }
    this.snackBar.error(this.translate.instant('ProjectConfiguratorV2.Review.incomplete'));
    this.scrollToFirstError();
  }

  /** "Neue Anfrage": starts over with an empty configurator. */
  newRequest(): void {
    this.store.reset();
    this.files.reset();
    this.scrollToTop();
  }

  protected openDashboard(): void {
    if (this.projectId) this.navigate.go('project-dashboard-summary', { projectId: this.projectId });
  }

  back(): void {
    this.store.back();
    this.scrollToTop();
  }

  goTo(index: number): void {
    this.store.goTo(index);
    this.scrollToTop();
  }

  protected editStep(index: number): void {
    this.summaryOpen.set(false);
    this.goTo(index);
  }

  private scrollToTop(): void {
    this.top?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private scrollToFirstError(): void {
    const firstError = Object.keys(this.store.errors())[0];
    if (!firstError) return;

    // The step with the error may have to be rendered first.
    setTimeout(() =>
      this.host.nativeElement
        .querySelector(`[data-question-id="${firstError}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    );
  }
}
